/**
 * Multi-Select Control
 * Multiple selection dropdown field
 * Performance: Memoized, useCallback + useMemo for options
 */

import React, { useCallback, useMemo, memo } from 'react';
import { WarningIcon } from '@atlas-forms/ui-components-react';
import type { ControlRendererProps } from '../ControlRenderer';
import { getBaseControlClassName, getSelectClassName } from '../utils/classNameUtils';

interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

const MultiSelectInput = memo(React.forwardRef<
  HTMLSelectElement,
  ControlRendererProps
>(
  (
    {
      control,
      value,
      onChange,
      onBlur,
      onFocus,
      error,
      touched,
      disabled,
      mode,
    },
    ref,
  ) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = Array.from(e.target.selectedOptions, (option) =>
          option.value,
        );
        onChange(selected);
      },
      [onChange],
    );

    const showError = touched && error;
    const containerClassName = getBaseControlClassName(control, {
      error: showError,
      disabled: disabled || control.disabled,
    });

    // Get options from control config
    const options = useMemo<SelectOption[]>(() => {
      const configOptions = (control.config?.options as SelectOption[]) || [];
      return Array.isArray(configOptions) ? configOptions : [];
    }, [control.config]);

    // Ensure value is always an array
    const selectedValues = Array.isArray(value) ? value : [];

    const selectClassName = getSelectClassName({
      error: showError,
      disabled: disabled || control.disabled,
    });

    return (
      <div className={containerClassName}>
        {control.label && (
          <label htmlFor={control.id} className="control__label">
            <span className="control__label-text">{control.label}</span>
            {control.required && (
              <span className="control__required" aria-label="required">
                *
              </span>
            )}
          </label>
        )}

        <div className="control__select-wrapper">
          <select
            ref={ref}
            id={control.id}
            name={control.id}
            value={selectedValues}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            disabled={disabled || control.disabled}
            multiple
            className={selectClassName}
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={`${control.id}-error ${control.id}-help`}
          >
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          {showError && (
            <span className="control__error-icon" aria-hidden="true">
              <WarningIcon width={14} height={14} />
            </span>
          )}
        </div>

        {control.helpText && (
          <div id={`${control.id}-help`} className="control__help">
            {control.helpText}
          </div>
        )}

        {showError && (
          <div
            id={`${control.id}-error`}
            className="control__error-message"
            role="alert"
          >
            {error}
          </div>
        )}
      </div>
    );
  },
));

MultiSelectInput.displayName = 'MultiSelectInput';

export default MultiSelectInput;
